import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ChatResponse } from '../chat-bot/chat-bot.service';

export interface AdminSummary {
  products: number;
  categories: number;
  series: number;
  productsWithAssets: number;
  faq: number;
  articleKnowledge: number;
  areaLocations: number;
  missingQuestions: number;
  chatRecordingEnabled: boolean;
  chatRecordings: number;
}

export interface AdminProduct {
  code: string;
  displayCode: string;
  name: string;
  category: string;
  series: string;
  active: boolean;
  description: string;
  dimension: string;
  coverage: string;
  applications: string[];
  availablePatterns: string;
  defaultPattern: string;
  imageUrl: string;
  textureUrl: string;
  thumbnailUrl: string;
  hiresUrl: string;
  view360Url: string;
}

export interface ProductListResponse {
  products: AdminProduct[];
  total: number;
  limit: number;
  offset: number;
  categories: string[];
  seriesByCategory: Record<string, string[]>;
}

export interface MissingQuestion {
  message: string;
  reason: string;
  count: number;
  firstAskedAt: string;
  lastAskedAt: string;
}

export interface ChatRecording {
  id: string;
  recordedAt: string;
  sessionId: string;
  source: string;
  statusCode: number;
  question: string;
  answer: string;
  products: Array<{
    code: string;
    displayCode: string;
    name: string;
    category: string;
    series: string;
    hiresUrl: string;
    view360Url: string;
  }>;
  salesContacts: Array<{
    fullName: string;
    nickname: string;
    phone: string;
    email: string;
    area: string;
  }>;
  sourceCount: number;
  attachmentCount: number;
}

export interface ChatRecordingResponse {
  enabled: boolean;
  count: number;
  filteredCount: number;
  recordings: ChatRecording[];
  path: string;
}

export interface AnythingLlmDocument {
  id: string;
  docId?: string;
  name: string;
  title: string;
  docpath: string;
  folder?: string;
  published?: string;
  wordCount?: number;
  tokenCount?: number;
  source?: string;
  cached?: boolean;
  inWorkspace?: boolean;
}

export interface AnythingLlmDocumentsResponse {
  workspace: {
    name: string;
    slug: string;
    documentCount: number;
  };
  localDocuments: AnythingLlmDocument[];
  workspaceDocuments: AnythingLlmDocument[];
}

export interface FaqSyncResponse {
  faqCount: number;
  output: string;
  errorOutput: string;
}

export interface AreaSyncResponse {
  areaCount: number;
  output: string;
  errorOutput: string;
}

@Injectable({
  providedIn: 'root',
})
export class AdminPanelService {
  constructor(private http: HttpClient) {}

  getSummary(): Observable<AdminSummary> {
    return this.http.get<AdminSummary>('/api/admin/summary');
  }

  getProducts(filters: { search?: string; category?: string; series?: string; limit?: number; offset?: number }) {
    let params = new HttpParams();

    for (const [key, value] of Object.entries(filters)) {
      if (value !== undefined && value !== null && String(value).trim()) {
        params = params.set(key, String(value));
      }
    }

    return this.http.get<ProductListResponse>('/api/admin/products', { params });
  }

  updateProduct(product: AdminProduct): Observable<ProductListResponse> {
    return this.http.put<ProductListResponse>(`/api/admin/products/${encodeURIComponent(product.code)}`, {
      ...product,
    });
  }

  updateProductAssets(product: AdminProduct) {
    return this.http.put<{ product: AdminProduct }>(`/api/admin/products/${encodeURIComponent(product.code)}/assets`, {
      thumbnailUrl: product.thumbnailUrl,
      hiresUrl: product.hiresUrl,
      view360Url: product.view360Url,
    });
  }

  testChat(message: string, sessionId: string): Observable<ChatResponse> {
    return this.http.post<ChatResponse>('/api/chat', {
      message,
      sessionId,
    });
  }

  getMissingQuestions() {
    return this.http.get<{ questions: MissingQuestion[] }>('/api/admin/missing-questions');
  }

  clearMissingQuestions() {
    return this.http.delete<{ questions: MissingQuestion[] }>('/api/admin/missing-questions');
  }

  getChatRecording(filters?: { startDate?: string; endDate?: string }) {
    let params = new HttpParams();

    for (const [key, value] of Object.entries(filters || {})) {
      if (value) {
        params = params.set(key, value);
      }
    }

    return this.http.get<ChatRecordingResponse>('/api/admin/chat-recording', { params });
  }

  setChatRecording(enabled: boolean) {
    return this.http.put<ChatRecordingResponse>('/api/admin/chat-recording', { enabled });
  }

  clearChatRecording() {
    return this.http.delete<ChatRecordingResponse>('/api/admin/chat-recording');
  }

  getChatRecordingExportUrl(filters?: { startDate?: string; endDate?: string }) {
    let params = new HttpParams();

    for (const [key, value] of Object.entries(filters || {})) {
      if (value) {
        params = params.set(key, value);
      }
    }

    const query = params.toString();
    return `/api/admin/chat-recording/export${query ? `?${query}` : ''}`;
  }

  getDocuments() {
    return this.http.get<AnythingLlmDocumentsResponse>('/api/admin/documents');
  }

  uploadDocument(file: { filename: string; mimeType: string; base64: string }) {
    return this.http.post<AnythingLlmDocumentsResponse>('/api/admin/documents/upload', file);
  }

  syncWorkspaceDocuments(payload: { adds?: string[]; deletes?: string[] }) {
    return this.http.post<AnythingLlmDocumentsResponse>('/api/admin/documents/workspace', payload);
  }

  syncFaqKnowledge() {
    return this.http.post<FaqSyncResponse>('/api/admin/faq/sync', {});
  }

  syncIndonesiaAreas() {
    return this.http.post<AreaSyncResponse>('/api/admin/areas/sync', {});
  }
}
